import { Alert, Button, Form, Modal, Typography } from 'antd';

import { useNewUserAccessRights } from '@app/hooks';
import { TimeInput } from '@app/shared/components';

import {
  useEmployeeBreakActionModal,
  useEmployeeBreakActionModalEffects,
} from '../hooks';
import {
  MIN_BREAK_DURATION_MINUTES,
  getTimeEndRules,
  getTimeStartRules,
} from '../utils';

import '../styles/EmployeeBreakActionModal.scss';

import type { FC } from 'react';

interface EmployeeBreakActionModalProps {
  breakId?: number;
  date: string;
  employeeId: number;
  employeeName: string;
  initialTimeS: string;
  initialTimeE: string;
  isOpen: boolean;
  mode: 'create' | 'edit';
  onClose(): void;
  onSuccess(): void;
}

const { Text } = Typography;

export const EmployeeBreakActionModal: FC<EmployeeBreakActionModalProps> = ({
  breakId,
  date,
  employeeId,
  employeeName,
  initialTimeS,
  initialTimeE,
  isOpen,
  mode,
  onClose,
  onSuccess,
}) => {
  const {
    userAccessRights: {
      WorkSchedules: { IsCreateBreak, IsDeleteBreak, IsEditBreak },
    },
  } = useNewUserAccessRights();
  const {
    form,
    warningText,
    currentBreakLabel,
    selectedDateLabel,
    isLoading,
    handleClose,
    handleConfirm,
    handleDelete,
    handleFinish,
    syncFormAndCheckBreakConflicts,
  } = useEmployeeBreakActionModal({
    breakId,
    date,
    employeeId,
    initialTimeS,
    initialTimeE,
    isOpen,
    mode,
    onClose,
    onSuccess,
  });

  useEmployeeBreakActionModalEffects({
    syncFormAndCheckBreakConflicts,
  });

  const isEditMode = mode === 'edit';
  const canSave = isEditMode ? IsEditBreak : IsCreateBreak;

  return (
    <Modal
      className="EmployeeBreakActionModal"
      open={isOpen}
      destroyOnHidden
      width={520}
      onCancel={handleClose}
      title={
        <div className="EmployeeBreakActionModal__title">
          <span className="EmployeeBreakActionModal__title-text">
            {isEditMode ? 'Изменить перерыв' : 'Добавить перерыв'}
          </span>
          <span className="EmployeeBreakActionModal__title-hint">
            {isEditMode
              ? 'Скорректируйте интервал или удалите перерыв из расписания'
              : 'Укажите интервал перерыва в рамках рабочего дня исполнителя'}
          </span>
        </div>
      }
      data-testid={
        isEditMode ? 'calendar-edit-break-popup' : 'calendar-add-break-popup'
      }
      footer={[
        isEditMode && (
          <Button
            key="delete"
            data-testid="delete"
            danger
            loading={isLoading}
            disabled={isLoading || !IsDeleteBreak}
            onClick={handleDelete}
          >
            Удалить
          </Button>
        ),
        <Button
          key="cancel"
          data-testid="cancel"
          disabled={isLoading}
          onClick={handleClose}
        >
          Отмена
        </Button>,
        <Button
          key="save"
          data-testid="save"
          type="primary"
          loading={isLoading}
          disabled={isLoading || !canSave}
          onClick={handleConfirm}
        >
          Сохранить
        </Button>,
      ]}
    >
      <div className="EmployeeBreakActionModal__content">
        <div className="EmployeeBreakActionModal__summary">
          <div className="EmployeeBreakActionModal__summary-card">
            <Text className="EmployeeBreakActionModal__summary-label">
              Исполнитель
            </Text>
            <Text className="EmployeeBreakActionModal__summary-value">
              {employeeName}
            </Text>
          </div>

          <div className="EmployeeBreakActionModal__summary-card">
            <Text className="EmployeeBreakActionModal__summary-label">
              Дата
            </Text>
            <Text className="EmployeeBreakActionModal__summary-value">
              {selectedDateLabel}
            </Text>
          </div>
        </div>

        {isEditMode && (
          <div className="EmployeeBreakActionModal__time-grid">
            <div className="EmployeeBreakActionModal__delete-card">
              <Text className="EmployeeBreakActionModal__summary-label">
                Текущий перерыв
              </Text>
              <Text className="EmployeeBreakActionModal__summary-value">
                {currentBreakLabel}
              </Text>
            </div>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="EmployeeBreakActionModal__form"
        >
          <div className="EmployeeBreakActionModal__time-grid">
            <Form.Item name="timeS" label="От" rules={getTimeStartRules()}>
              <TimeInput data-testid="time-start" />
            </Form.Item>

            <Form.Item
              name="timeE"
              label="До"
              dependencies={['timeS']}
              rules={getTimeEndRules(MIN_BREAK_DURATION_MINUTES)}
            >
              <TimeInput data-testid="time-end" />
            </Form.Item>
          </div>
        </Form>

        {!!warningText && (
          <Alert
            className="EmployeeBreakActionModal__warning"
            message={warningText}
            type="warning"
            showIcon
          />
        )}
      </div>
    </Modal>
  );
};
